//Jan21 DropdownDependenciesContext.js without bug fix:
import React, { createContext, useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import isEqual from 'react-fast-compare';


import { 
  // NOTE: getDropdownDependencies / updateFormDropdownDependencies are now *legacy* /
  // optional helpers. The canonical source for dependencies is the compressed
  // `dropdownDependencies` field on the `forms/{formId}` document.
  getDropdownDependencies, 
  getDropdownDependenciesForField, 
  updateFormDropdownDependencies 
} from '../utils/firestore';
import { safeSetItem, safeGetItem } from '../utils/storageUtils';
import { useFormContext } from './FormContext';
import { useFieldMetadata } from './FieldMetadataContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { decompressData } from '../utils/decompressionUtils';
import { useMultiAnswerField } from './MultiAnswerFieldContext';
//import { debounce } from 'lodash';

// managed-field instance detection
import { isOrgManagedDropdownInstance } from '../utils/managedFieldInstances';

// version-aware blob selection (draftOverlay vs root) aligned with other contexts
import { resolveDropdownDependencies, getFormVersionSnapshot } from '../utils/formVersionResolver';

const DropdownDependenciesContext = createContext();

export const useDropdownDependencies = () => useContext(DropdownDependenciesContext);

export const DropdownDependenciesProvider = ({ children }) => {
  const { formId } = useFormContext();
  const { fieldsMetadata } = useFieldMetadata();
  const { dropdownOptions } = useMultiAnswerField();

  // ────────────────────────────────────────────────────────────────
  // Dropdown field grouping helpers
  //
  // IMPORTANT
  //  • FieldMetadataContext remains the source of truth for field metadata
  //  • MultiAnswerFieldContext remains the source of truth for dropdown options
  //  • DropdownDependenciesContext remains the source of truth for dependencies
  //
  // OWNERSHIP MODEL
  //  • Dependencies remain FORM-SPECIFIC for both local and org-managed fields
  //  • We do NOT store org-managed dependencies centrally
  //  • We DO allow org-managed field instances to participate in dependency
  //    graphs inside the current form
  //
  // WHY WE DERIVE FROM fieldsMetadata
  //  • dropdownOptions is an options map keyed by optionsSetUFI
  //  • it does NOT tell us field ownership / governance / org-managed status
  //  • fieldsMetadata is the correct source for identifying:
  //      - dropdown field type
  //      - org-managed vs local instance
  //      - optionsSetUFI binding
  // ────────────────────────────────────────────────────────────────
  const allDropdownFieldMetadata = useMemo(() => {
    return Object.values(fieldsMetadata || {}).filter(
      (field) =>
        field?.type === 'single-select' ||
        field?.type === 'multi-select'
    );
  }, [fieldsMetadata]);

  const managedDropdownFieldMetadata = useMemo(() => {
    return allDropdownFieldMetadata.filter((field) =>
      isOrgManagedDropdownInstance(field)
    );
  }, [allDropdownFieldMetadata]);

  const localDropdownFieldMetadata = useMemo(() => {
    return allDropdownFieldMetadata.filter(
      (field) => !isOrgManagedDropdownInstance(field)
    );
  }, [allDropdownFieldMetadata]);

  // Convenience lookup by optionsSetUFI.
  // This is useful when dependency logic needs to understand whether a given
  // dropdown set belongs to a local field instance or an org-managed field instance.
  const dropdownFieldMetadataByOptionsSetUFI = useMemo(() => {
    const map = {};

    allDropdownFieldMetadata.forEach((field) => {
      const optionsSetUFI = field?.optionsSetUFI;
      if (!optionsSetUFI) return;

      map[optionsSetUFI] = field;
    });

    return map;
  }, [allDropdownFieldMetadata]);

  const [dropdownDependencies, _setDropdownDependencies] = useState({});
  const dropdownDependenciesRef = useRef({});

  const setDropdownDependencies = useCallback(
    (producer) => {
      _setDropdownDependencies((prev) => {
        const next =
          typeof producer === 'function' ? producer(prev) : producer;

        const resolvedNext = isEqual(prev, next) ? prev : next;

        dropdownDependenciesRef.current = resolvedNext || {};

        return resolvedNext;
      });
    },
    []
  );

  useEffect(() => {
    dropdownDependenciesRef.current = dropdownDependencies || {};
  }, [dropdownDependencies]);

  const [loading, setLoading] = useState(false);
  /** ----------  STEP‑2  per‑UFI loading map  ---------- */
  const [loadingMap, setLoadingMap] = useState({});   // { [optionsSetUFI]: boolean }

  // tiny helper so we never create a new object when nothing changed
  const setUfiLoading = useCallback((ufi, value) => {
    setLoadingMap(prev =>
      prev[ufi] === value ? prev : { ...prev, [ufi]: value }
    );
  }, []);

const [error, setError] = useState(null);

// dynamicDropdownOptions is NOT a second source of truth.
// It is a derived view of canonical dropdownOptions, optionally overridden by active dependency filtering.
// We store only the active overrides; the "dynamic" map is computed from canonical + overrides.
const [filteredOverridesBySet, setFilteredOverridesBySet] = useState({}); // { [optionsSetUFI]: Option[] }

// derive dynamicDropdownOptions from canonical dropdownOptions + overrides.
// This prevents drift when Firestore loads "real" options that happen to have the same length
// as seeded defaults (e.g., 3 default options → 3 real options).
const dynamicDropdownOptions = useMemo(() => {
  const base = dropdownOptions || {};
  const overrides = filteredOverridesBySet || {};

  // Merge policy:
  // - default to base for all known sets
  // - override only the sets present in overrides
  // - include any override keys not present in base (defensive)
  const merged = { ...base };

  Object.entries(overrides).forEach(([setId, opts]) => {
    merged[setId] = Array.isArray(opts) ? opts : [];
  });

  return merged;
}, [dropdownOptions, filteredOverridesBySet]);

  // New: Initialize dependency flags to track which optionIds have dependencies
  const [dependencyFlags, setDependencyFlags] = useState({});


  // Track whether we've successfully hydrated dropdownDependencies from
  // Firestore or from a valid local-storage snapshot. This lets us avoid
  // seeding empty objects during the very first load of a published form.
  const [hasHydratedFromFirestore, setHasHydratedFromFirestore] = useState(false);

  // ENTERPRISE:
  // Viewer/embed routes should hydrate dependencies from the canonical published
  // form-level blob, not from legacy per-field reads. Multiple field components
  // may ask for dependencies during the same first paint; this ref dedupes that
  // canonical fetch so we do not launch repeated reads.
  const canonicalViewerFetchInFlightRef = useRef(null);

  // ────────────────────────────────────────────────────────────────
  // Route-aware flags (builder vs viewer) – mirrors FormThemeContext
  // ────────────────────────────────────────────────────────────────
  const isPublicPreviewMode = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('isPublicPreview');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.value === true;
    } catch (e) {
      return false;
    }
  }, []);

  const isBuilderRoute = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.startsWith('/form-builder');
  }, []);

  // ────────────────────────────────────────────────────────────────
  // Mode-aware dropdownDependencies cache keys (aligned with FormLayoutContext)
  // - Builder route: honor builder view mode (auto/published/draftOverlay)
  // - Viewer route (/forms/:id): ALWAYS force published
  // ────────────────────────────────────────────────────────────────
  const builderViewMode = useMemo(() => {
    try {
      if (!formId) return 'auto';
      const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.value || 'auto'; // 'auto' | 'published' | 'draftOverlay'
    } catch (e) {
      return 'auto';
    }
  }, [formId]);

  const effectiveViewMode = useMemo(() => {
    if (isBuilderRoute) return builderViewMode;
    return 'published';
  }, [isBuilderRoute, builderViewMode]);

  const dependenciesKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_dropdownDependencies__${effectiveViewMode}`;
  }, [formId, effectiveViewMode]);

  const lastUpdatedKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_dropdownDependenciesLastUpdated__${effectiveViewMode}`;
  }, [formId, effectiveViewMode]);

  // Legacy keys (still used by not-yet-refactored paths)
  const legacyDependenciesKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_dropdownDependencies`;
  }, [formId]);

  const legacyLastUpdatedKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_dropdownDependenciesLastUpdated`;
  }, [formId]);

  // When mode changes, treat hydration as stale (prevents “seed empty” races)
  useEffect(() => {
    if (!formId) return;
    setHasHydratedFromFirestore(false);
  }, [formId, effectiveViewMode]);


  /**
   * Helper function to build dependency flags based on the current dropdownDependencies.
   * This function creates a lookup of optionIds with dependencies.
   */
  const buildDependencyFlags = useCallback((dependencies) => {
    const flags = {};
    Object.keys(dependencies).forEach((optionsSetUFI) => {
      Object.keys(dependencies[optionsSetUFI]).forEach((optionId) => {
      const entry = dependencies?.[optionsSetUFI]?.[optionId];
      flags[optionId] = Array.isArray(entry?.dependentOptionIds) && entry.dependentOptionIds.length > 0;
      });
    });
    return flags;
  }, []);

  /**
   * Keep dependencyFlags in sync with dropdownDependencies.
   * This ensures that after hydration from Firestore or local storage,
   * the UI (tree icons) reflects the actual dependency state.
   */
  useEffect(() => {
    // If there are no dependencies at all, clear flags (once) and exit
    if (!dropdownDependencies || Object.keys(dropdownDependencies).length === 0) {
      setDependencyFlags((prev) =>
        Object.keys(prev).length === 0 ? prev : {}
      );
      return;
    }

    const flags = buildDependencyFlags(dropdownDependencies);

    // Avoid unnecessary re-renders if nothing actually changed
    setDependencyFlags((prevFlags) =>
      isEqual(prevFlags, flags) ? prevFlags : flags
    );
  }, [dropdownDependencies, buildDependencyFlags]);


const fetchDropdownDependencies = useCallback(async () => {
  if (!formId) {
    console.warn("[DropdownDependenciesProvider] No formId provided, skipping dropdown dependencies fetch.");
    return;
  }

  setLoading(true);

  // Mode-aware active keys + legacy fallback
  const activeDependenciesKey = dependenciesKey || `form_${formId}_dropdownDependencies__published`;
  const activeLastUpdatedKey  = lastUpdatedKey  || `form_${formId}_dropdownDependenciesLastUpdated__published`;

  const legacyKey       = legacyDependenciesKey || `form_${formId}_dropdownDependencies`;
  const legacyUpdatedKey= legacyLastUpdatedKey  || `form_${formId}_dropdownDependenciesLastUpdated`;

  // Step 1: Check for stored dropdown dependencies (cache)
  const storedDependencies =
    safeGetItem(activeDependenciesKey) ||
    safeGetItem(legacyKey);

  const localLastUpdated =
    safeGetItem(activeLastUpdatedKey) ||
    safeGetItem(legacyUpdatedKey);

  if (storedDependencies && Object.keys(storedDependencies).length > 0) {
    setDropdownDependencies(storedDependencies);
    setDependencyFlags(buildDependencyFlags(storedDependencies));

    // ✅ Mark that we've hydrated from a valid snapshot
    setHasHydratedFromFirestore(true);

    // Builder/public-preview: allow storage to short-circuit.
    // Viewer: DO NOT return here – fall through so Firestore can refresh stale deps (e.g., AUF).
    if (isBuilderRoute || isPublicPreviewMode) {
      setLoading(false);
      return;
    }
  }

  // Step 2: Fetch from Firestore (canonical, version-aware)
  try {
    const formRef = doc(db, 'forms', formId);
    const formDoc = await getDoc(formRef);

    if (!formDoc.exists()) {
      console.warn(`[DropdownDependenciesProvider] No form found for formId: ${formId} when fetching dropdown dependencies.`);
      setLoading(false);
      return;
    }

    const formData = formDoc.data() || {};

    // NEW: version-aware resolve for dropdownDependencies (draftOverlay vs root)
    const versionSnap  = getFormVersionSnapshot(formData);
    const resolvedDeps = resolveDropdownDependencies(formData, { viewMode: effectiveViewMode, debug: true });

    console.log('[DDC:fetchDropdownDependencies] version snapshot:', versionSnap);
    console.log('[DDC:fetchDropdownDependencies] resolved dropdownDependencies source:', resolvedDeps);

    const rawDropdownDependencies = resolvedDeps?.value;

    // Source-aware lastUpdated
    const firestoreLastUpdated = (() => {
      try {
        if (resolvedDeps?.source === 'draftOverlay') {
          const t =
            formData?.draftOverlaySavedAt ||
            formData?.draftOverlay?.savedAt ||
            null;
          if (t && typeof t.toDate === 'function') return t.toDate().toISOString();
          if (t instanceof Date) return t.toISOString();
        }
        const lm = formData?.lastModified;
        if (lm && typeof lm.toDate === 'function') return lm.toDate().toISOString();
        if (lm instanceof Date) return lm.toISOString();
      } catch (e) {}
      return null;
    })();

    if (!rawDropdownDependencies) {
      console.warn(
        `[DropdownDependenciesProvider] No resolved dropdownDependencies blob found for formId: ${formId} (viewMode=${effectiveViewMode})`
      );
      setLoading(false);
      return;
    }

    // Timestamp Validation (ONLY builder/public-preview). Viewer always trusts Firestore.
    if (
      (isBuilderRoute || isPublicPreviewMode) &&
      localLastUpdated &&
      firestoreLastUpdated &&
      localLastUpdated > firestoreLastUpdated
    ) {
      console.warn(
        `[DDC:fetchDropdownDependencies] Local dropdown dependencies are newer than Firestore. Skipping Firestore overwrite.`,
        { localLastUpdated, firestoreLastUpdated }
      );
      setLoading(false);
      return;
    }

    // Decompress Firestore data (Base64 string -> object) OR accept object directly
    const decompressedDropdownDependencies = await decompressData(rawDropdownDependencies);

    // ✅ Store in state
    setDropdownDependencies(decompressedDropdownDependencies);
    setDependencyFlags(buildDependencyFlags(decompressedDropdownDependencies));

    // ✅ Cache mode-aware (authoritative) + legacy mirror (temporary compatibility)
    safeSetItem(activeDependenciesKey, decompressedDropdownDependencies);
    safeSetItem(activeLastUpdatedKey, firestoreLastUpdated || new Date().toISOString());

    safeSetItem(legacyKey, decompressedDropdownDependencies);
    safeSetItem(legacyUpdatedKey, firestoreLastUpdated || new Date().toISOString());

    setHasHydratedFromFirestore(true);

  } catch (err) {
    console.error("[DropdownDependenciesProvider] Error fetching dropdown dependencies from Firestore:", err);
    setError(err?.message || String(err));
  } finally {
    setLoading(false);
  }
}, [
  formId,
  buildDependencyFlags,
  isBuilderRoute,
  isPublicPreviewMode,
  effectiveViewMode,
  dependenciesKey,
  lastUpdatedKey,
  legacyDependenciesKey,
  legacyLastUpdatedKey,
]);





/**
 * Persist dropdown dependencies for a specific optionsSetUFI to local storage
 * without updating React state.
 *
 * WHY THIS EXISTS
 * -----------------------------------------------------------------------------
 * updateDependenciesForOption(...) already owns the state update for the
 * dependency edit currently being performed by the side panel.
 *
 * Calling saveDropdownDependenciesToStorage(...) from inside that state updater
 * caused nested state updates because saveDropdownDependenciesToStorage(...)
 * also called setDropdownDependencies(...).
 *
 * ENTERPRISE RULE
 *  • React state updates stay pure.
 *  • Storage persistence is a side effect performed after the next state object
 *    is computed.
 *  • Public save/publish behavior is unchanged because this still writes the
 *    same mode-aware + legacy storage keys.
 */
const persistDropdownDependenciesSetToStorage = useCallback((optionsSetUFI, dependenciesData) => {
  if (!formId || !optionsSetUFI || !dependenciesData) {
    console.warn("[DropdownDependenciesProvider] Invalid parameters. Skipping save operation.", {
      formId,
      optionsSetUFI,
      hasDependenciesData: !!dependenciesData,
    });
    return null;
  }

  // NEW: mode-aware keys (aligned with FormLayoutContext)
  const activeKey =
    dependenciesKey || `form_${formId}_dropdownDependencies__published`;
  const activeUpdatedKey =
    lastUpdatedKey || `form_${formId}_dropdownDependenciesLastUpdated__published`;

  const legacyKey =
    legacyDependenciesKey || `form_${formId}_dropdownDependencies`;
  const legacyUpdatedKey =
    legacyLastUpdatedKey || `form_${formId}_dropdownDependenciesLastUpdated`;

  // Retrieve existing dependencies from storage (mode-aware first, then legacy)
  const storedDropdownDependencies =
    safeGetItem(activeKey) ||
    safeGetItem(legacyKey) ||
    {};

  const nextStoredDropdownDependencies = {
    ...(storedDropdownDependencies || {}),
    [optionsSetUFI]: dependenciesData,
  };

  // Save updated dependencies back to storage (mode-aware + legacy mirror)
  safeSetItem(activeKey, nextStoredDropdownDependencies);
  safeSetItem(activeUpdatedKey, new Date().toISOString());

  safeSetItem(legacyKey, nextStoredDropdownDependencies);
  safeSetItem(legacyUpdatedKey, new Date().toISOString());

  return nextStoredDropdownDependencies;
}, [
  formId,
  dependenciesKey,
  lastUpdatedKey,
  legacyDependenciesKey,
  legacyLastUpdatedKey,
]);

/**
 * Save dropdown dependencies for a specific optionsSetUFI to local storage.
 * This prevents unnecessary Firestore writes and enables quick UI updates.
 */
const saveDropdownDependenciesToStorage = useCallback((optionsSetUFI, dependenciesData) => {
  const persistedSnapshot = persistDropdownDependenciesSetToStorage(
    optionsSetUFI,
    dependenciesData,
  );

  if (!persistedSnapshot) {
    return;
  }

  // Update state outside of any other React state updater.
  setDropdownDependencies((prev) => ({
    ...prev,
    [optionsSetUFI]: dependenciesData,
  }));
}, [
  persistDropdownDependenciesSetToStorage,
  setDropdownDependencies,
]);


/**
 * Load dependencies for a specific optionsSetUFI on demand.
 */
const loadDependenciesForField = useCallback(async (optionsSetUFI) => {
  if (!formId || !optionsSetUFI) {
    console.error("[DropdownDependenciesProvider] formId or optionsSetUFI is missing.");
    return;
  }

  // IMPORTANT:
  // For brand-new / unsaved forms, "no form doc yet" is a normal state.
  // In that case, dependency loading should be quiet and simply use
  // whatever is already present in memory/storage.
  setUfiLoading(optionsSetUFI, true);   // ⬅ per-UFI flag

  // ---------------------------------------------------------------------------
  // ENTERPRISE VIEWER / EMBED OPTIMIZATION
  // ---------------------------------------------------------------------------
  // Published viewer routes already hydrate the canonical dropdownDependencies
  // snapshot from forms/{formId}.dropdownDependencies through
  // fetchDropdownDependencies().
  //
  // Do NOT fall back to legacy per-field Firestore reads on viewer routes.
  // Those reads are unnecessary for public embeds and public published forms,
  // and create avoidable network work like:
  //
  //   getDropdownDependenciesForField(formId, optionsSetUFI)
  //
  // Builder/public-preview routes keep the legacy per-field fallback below.
  // ---------------------------------------------------------------------------
  const isViewerRoute = !isBuilderRoute && !isPublicPreviewMode;

  if (isViewerRoute) {
    try {
      if (!hasHydratedFromFirestore) {
        if (!canonicalViewerFetchInFlightRef.current) {
          canonicalViewerFetchInFlightRef.current = fetchDropdownDependencies()
            .finally(() => {
              canonicalViewerFetchInFlightRef.current = null;
            });
        }

        await canonicalViewerFetchInFlightRef.current;
      }
    } catch (error) {
      console.warn(
        `[DropdownDependenciesProvider] Viewer route canonical dependency fetch failed for optionsSetUFI ${optionsSetUFI}.`,
        {
          formId,
          optionsSetUFI,
          message: error?.message || String(error),
        }
      );
    } finally {
      setUfiLoading(optionsSetUFI, false);
    }

    return;
  }

  try {
    // Step 1: Attempt to retrieve from local storage first
    const activeKey = dependenciesKey || `form_${formId}_dropdownDependencies__published`;
    const legacyKey = legacyDependenciesKey || `form_${formId}_dropdownDependencies`;

    const storedDependencies =
      safeGetItem(activeKey) ||
      safeGetItem(legacyKey) ||
      {};

    if (storedDependencies?.[optionsSetUFI]) {
//      console.log(`[DropdownDependenciesProvider] Using cached dependencies for optionsSetUFI ${optionsSetUFI} from local storage.`);
      setDropdownDependencies(prev => ({
        ...prev,
        [optionsSetUFI]: storedDependencies[optionsSetUFI],
      }));

      // Builder/public-preview: cache can short-circuit.
      // Viewer: rely on the canonical dropdownDependencies snapshot already
      // hydrated by fetchDropdownDependencies; do NOT refresh per-field from Firestore.
      if (isBuilderRoute || isPublicPreviewMode) {
        return;
      }

      if (!isBuilderRoute && !isPublicPreviewMode) {
//        console.log(
//          `[DropdownDependenciesProvider] Viewer route – using canonical dropdownDependencies, ` +
//          `skipping per-field Firestore fetch for optionsSetUFI ${optionsSetUFI}.`
//        );
        return;
      }
    }

    // NOTE: For viewer routes, we never reach this point because we either:
    // - short-circuit on builder/public-preview (above), or
    // - short-circuit on viewer (canonical) when stored deps exist.
    // Step 2: Fetch dependencies from Firestore if not found in local storage
    // IMPORTANT:
    // - Missing dependency docs are normal for new forms
    // - Missing form docs are also normal for unsaved builder sessions
    // - Treat both as "no dependencies yet", not as a noisy failure state
//        console.log(`[DropdownDependenciesProvider] Fetching dependencies for optionsSetUFI ${optionsSetUFI} from Firestore...`);

    let fieldDependencies = {};

    try {
      fieldDependencies = await getDropdownDependenciesForField(formId, optionsSetUFI);
    } catch (error) {
      const message = error?.message || String(error);

      const isExpectedMissingFormState =
        message.includes('No form found') ||
        message.includes('not found') ||
        message.includes('No dependencies found');

      if (isExpectedMissingFormState) {
        console.warn(
          `[DropdownDependenciesProvider] Treating missing dependency doc as empty dependency set for optionsSetUFI ${optionsSetUFI}.`,
          { formId, optionsSetUFI, message }
        );
        fieldDependencies = {};
      } else {
        throw error;
      }
    }

    // Step 3: Update state
    setDropdownDependencies(prev => ({
      ...prev,
      [optionsSetUFI]: fieldDependencies,
    }));

    // Step 4: Save to local storage for future use
    const updatedStorageData = { ...storedDependencies, [optionsSetUFI]: fieldDependencies };
    safeSetItem(activeKey, updatedStorageData);
    safeSetItem(legacyKey, updatedStorageData); // mirror during refactor

//        console.log(`[DropdownDependenciesProvider] Successfully fetched and cached dependencies for optionsSetUFI ${optionsSetUFI}.`);
  } catch (error) {
    console.error(`[DropdownDependenciesProvider] Error loading dependencies for optionsSetUFI ${optionsSetUFI}:`, error);
    setError(error?.message || String(error));
  } finally {
  setUfiLoading(optionsSetUFI, false);
}

}, [
  formId,
  setUfiLoading,
  isBuilderRoute,
  isPublicPreviewMode,
  hasHydratedFromFirestore,
  fetchDropdownDependencies,
  effectiveViewMode,
  dependenciesKey,
  lastUpdatedKey,
  legacyDependenciesKey,
  legacyLastUpdatedKey,
  dropdownOptions,
  fieldsMetadata,
  managedDropdownFieldMetadata,
  localDropdownFieldMetadata,
]);




/**
 * Get dependencies for a specific optionId within an optionsSetUFI.
 * This function identifies which options in other fields should be filtered based on the selected option.
 */
const getDependenciesForOption = useCallback((optionsSetUFI, optionId) => {
//  console.log('[DropdownDependenciesProvider: getDependenciesForOption] Current dropdownDependencies structure:', dropdownDependencies);

  if (!dropdownDependencies[optionsSetUFI] || !dropdownDependencies[optionsSetUFI][optionId]) {
//    console.log(`[DropdownDependenciesProvider: getdependenciesforoption] No dependencies found for optionId ${optionId} in optionsSetUFI ${optionsSetUFI}`);
    return [];
  }
  
  // Log the retrieved dependencies for transparency in dependency tracking
  const dependencies = dropdownDependencies[optionsSetUFI][optionId].dependentOptionIds || [];
//  console.log(`[DropdownDependenciesProvider: getdependenciesforoption] Retrieved dependencies for optionId ${optionId} in optionsSetUFI ${optionsSetUFI}:`, dependencies);
  return dependencies;
}, [dropdownDependencies]);

/**
 * Get filtered options for dependent fields based on the dependencies in the specified `optionsSetUFI`.
 * This ensures that only dependent fields (not the field making the selection) are updated with filtered options.
 * @param {string} triggeringOptionsSetUFI - UFI of the field triggering the dependency
 * @param {string} triggeringOptionId - The option ID that triggers the dependency
 * @returns {object} An object mapping each dependent optionsSetUFI to its filtered options array
 */
const getFilteredOptions = useCallback((triggeringOptionsSetUFI, triggeringOptionId) => {
//  console.log(`[DropdownDependenciesProvider: getFilteredOptions] Starting with triggeringOptionsSetUFI: ${triggeringOptionsSetUFI} and triggeringOptionId: ${triggeringOptionId}`);

  if (!triggeringOptionId) {
    console.warn(`[DropdownDependenciesProvider: getFilteredOptions] Warning: triggeringOptionId is empty, no filtering will be applied.`);
    return {};
  }

  // Step 1: Retrieve dependencies for the triggering optionId in its optionsSetUFI
  const dependencies = getDependenciesForOption(triggeringOptionsSetUFI, triggeringOptionId);
//  console.log(`[DropdownDependenciesProvider: getFilteredOptions] Retrieved dependencies for triggeringOptionId ${triggeringOptionId} in triggeringOptionsSetUFI ${triggeringOptionsSetUFI}:`, dependencies);

  // Step 2: Check if dependencies exist; if not, return empty result indicating no filtering
  if (!dependencies || dependencies.length === 0) {
//    console.log(`[DropdownDependenciesProvider: getFilteredOptions] No dependencies found for triggeringOptionId ${triggeringOptionId}, no dependent fields will be filtered.`);
    return {};
  }

  // Step 3: Create a result object to hold filtered options for each dependent optionsSetUFI
  const filteredResults = {};

  // Step 4: Iterate over all dropdown options sets to find dependent fields
  Object.keys(dropdownOptions).forEach((dependentOptionsSetUFI) => {
    // Check if the dependentOptionsSetUFI contains any optionId that matches the dependencies
    const optionsToFilter = dropdownOptions[dependentOptionsSetUFI] || [];
    const filteredOptions = optionsToFilter.filter(option => dependencies.includes(option.optionId));
    
    // If there are matches, add them to the result for this dependent field
    if (filteredOptions.length > 0) {
      filteredResults[dependentOptionsSetUFI] = filteredOptions;
//      console.log(`[DropdownDependenciesProvider: getFilteredOptions] Filtered options for dependent optionsSetUFI ${dependentOptionsSetUFI}:`, filteredOptions);
    } else {
//      console.log(`[DropdownDependenciesProvider: getFilteredOptions] No matching options in dependent optionsSetUFI ${dependentOptionsSetUFI} based on dependencies for triggeringOptionId ${triggeringOptionId}.`);
    }
  });

  // Step 5: Log the complete set of filtered results for debugging
//  console.log(`[DropdownDependenciesProvider: getFilteredOptions] Final filtered results for dependent fields based on triggeringOptionId ${triggeringOptionId}:`, filteredResults);

  return filteredResults;
}, [dropdownOptions, getDependenciesForOption]);


/**
 * Notify dependent fields to re-render with filtered options based on triggering field selection.
 * This function applies dependencies to set filtered options in dependent fields.
 * If the triggering option is cleared (null or empty), restore full dropdown options for dependent fields.
 * @param {string} triggeringOptionsSetUFI - The UFI of the field whose selection triggers the dependency.
 * @param {string|null} triggeringOptionId - The ID of the selected option that triggers dependencies, or null if cleared.
 */
const notifyDependentFields = useCallback((triggeringOptionsSetUFI, triggeringOptionId) => {
//  console.log(`[DropdownDependenciesProvider: notifyDependentFields] Triggered with triggeringOptionsSetUFI: ${triggeringOptionsSetUFI}, triggeringOptionId: ${triggeringOptionId}`);

  if (!triggeringOptionId) {
    // If selection is cleared, remove all active dependency overrides so we fall back to canonical dropdownOptions.
    setFilteredOverridesBySet(prev => {
      // Keep a stable ref if already empty
      if (!prev || Object.keys(prev).length === 0) return prev;
      return {};
    });

    return;
  }

  // Otherwise, apply filtered options based on dependencies
  const filteredOptionsMap = getFilteredOptions(triggeringOptionsSetUFI, triggeringOptionId);
//  console.log(`[DropdownDependenciesProvider: notifyDependentFields] Filtered options map:`, filteredOptionsMap);

  setFilteredOverridesBySet(prev => {
    // Replace overrides with the currently active filtered map
    // (this matches your current behavior: a new trigger recalculates the dependent sets)
    if (isEqual(prev, filteredOptionsMap)) return prev;
    return filteredOptionsMap || {};
  });

}, [getFilteredOptions]);



  /**
   * Update dependencies for a specific optionId within an optionsSetUFI and update dependency flags.
   */
const updateDependenciesForOption = useCallback((optionsSetUFI, optionId, dependentOptionIds = []) => {
//  console.log('[DDC:updateDependenciesForOption] REQUEST', {
//    formId,
//    optionsSetUFI,
//    optionId,
//    dependentOptionIds,
//  });

  if (!optionsSetUFI || !optionId) {
    console.warn('[DDC:updateDependenciesForOption] Missing optionsSetUFI or optionId. Skipping update.', {
      formId,
      optionsSetUFI,
      optionId,
      dependentOptionIds,
    });
    return;
  }

  const safeDependentOptionIds = Array.isArray(dependentOptionIds)
    ? dependentOptionIds
    : [];

  const prevDependencies = dropdownDependenciesRef.current || {};
  const prevForSet = prevDependencies[optionsSetUFI] || {};
  const previousEntry = prevForSet?.[optionId] || null;
  const previousDependentOptionIds = Array.isArray(
    previousEntry?.dependentOptionIds
  )
    ? previousEntry.dependentOptionIds
    : [];

//  console.log('[DDC:updateDependenciesForOption] PREV for set', optionsSetUFI, {
//    prevForSet,
//  });

  /**
   * No-op guard
   * ---------------------------------------------------------------------------
   * If the option already has a dependency entry and the selected dependency
   * option IDs did not change, skip the state/storage work entirely.
   *
   * IMPORTANT
   *  • Missing entry + [] should still create the empty entry, preserving the
   *    existing behavior where dependency maps can contain initialized empty
   *    option records.
   *  • Existing entry + unchanged IDs should not rewrite storage timestamps or
   *    cause dependency-panel re-renders.
   */
  if (
    previousEntry &&
    isEqual(previousDependentOptionIds, safeDependentOptionIds)
  ) {
//    console.log('[DDC:updateDependenciesForOption] No dependency changes detected. Skipping update.', {
//      optionsSetUFI,
//      optionId,
//    });
    return;
  }

  const updatedForSet = {
    ...prevForSet,
    [optionId]: {
      dependentOptionIds: [...safeDependentOptionIds],
    },
  };

  const updatedDependencies = {
    ...prevDependencies,
    [optionsSetUFI]: updatedForSet,
  };

//  console.log('[DDC:updateDependenciesForOption] UPDATED for set', optionsSetUFI, {
//    updatedForSet,
//  });

  /**
   * ENTERPRISE STATE UPDATE RULE
   * ---------------------------------------------------------------------------
   * setDropdownDependencies(...) remains pure. We compute the next dependency
   * graph before calling React state setters and do not call any other setter
   * from inside the state updater.
   */
  dropdownDependenciesRef.current = updatedDependencies;
  setDropdownDependencies(updatedDependencies);

  // Update dependencyFlags logic (as you already do)
  setDependencyFlags((prevFlags) => {
    const updatedFlags = { ...prevFlags };
    if (safeDependentOptionIds.length > 0) {
      updatedFlags[optionId] = true;
    } else {
      delete updatedFlags[optionId];
    }
//    console.log('[DDC:updateDependenciesForOption] Updated dependencyFlags:', updatedFlags);
    return updatedFlags;
  });

  // Persist this single set to local storage without causing a nested state update.
  persistDropdownDependenciesSetToStorage(optionsSetUFI, updatedForSet);
}, [
  formId,
  persistDropdownDependenciesSetToStorage,
  setDropdownDependencies,
]);



/**
 * Effect to sync dependencies with dropdownOptions by initializing missing
 * dependency entries only.
 *
 * IMPORTANT ENTERPRISE BEHAVIOR
 * -----------------------------------------------------------------------------
 * We intentionally DO NOT delete dependency entries here when an optionId is
 * temporarily missing from dropdownOptions.
 *
 * WHY
 *  • In the V2 spreadsheet, a user can clear an option cell with Delete or
 *    Backspace and then retype it before saving/publishing.
 *  • Clearing the cell may temporarily remove that option from the current
 *    dropdownOptions snapshot.
 *  • If we delete its dependency entry immediately, we destroy valid dependency
 *    work before the user has intentionally saved or published the removal.
 *
 * FINAL CLEANUP OWNER
 *  • Save Draft / Publish perform deterministic dependency cleanup against the
 *    dropdownOptions snapshot that is actually being persisted.
 *  • That cleanup happens in formListsDependencyCleanupUtils.
 *
 * RESULT
 *  • Builder UX remains forgiving and spreadsheet-like.
 *  • Dependencies survive temporary local edits.
 *  • Persisted drafts/published forms still receive a clean dependency graph.
 */
useEffect(() => {
  // 🔒 Guard: don’t seed empty dependency objects while we’re still hydrating from Firestore
  if (!dropdownOptions || !formId || loading || !hasHydratedFromFirestore) return;

//  console.log('[DDC:syncWithDropdownOptions] Running sync effect', {
//    formId,
//    loading,
//    hasHydratedFromFirestore,
//    dropdownOptionSetIds: Object.keys(dropdownOptions || {}),
//  });

  setDropdownDependencies((prevDependencies) => {
    let changed = false;
    const updatedDependencies = { ...(prevDependencies || {}) };

    Object.keys(dropdownOptions || {}).forEach((optionsSetUFI) => {
      const optionsForSet = Array.isArray(dropdownOptions[optionsSetUFI])
        ? dropdownOptions[optionsSetUFI]
        : [];

      const existingForSet = updatedDependencies[optionsSetUFI] || {};
      let nextForSet = existingForSet;

      optionsForSet.forEach(({ optionId }) => {
        if (!optionId) return;

        // ✅ Check if the dependency exists before initializing.
        if (!existingForSet?.[optionId]) {
          if (nextForSet === existingForSet) {
            nextForSet = { ...existingForSet };
          }

//          console.log(`[DDC:syncWithDropdownOptions] Initializing empty dependencies for optionId ${optionId} in optionsSetUFI ${optionsSetUFI}`);
          nextForSet[optionId] = { dependentOptionIds: [] };
          changed = true;
        }
      });

      if (nextForSet !== existingForSet || !updatedDependencies[optionsSetUFI]) {
        updatedDependencies[optionsSetUFI] = nextForSet;
      }
    });

//    console.log('[DDC:syncWithDropdownOptions] FINAL updatedDependencies snapshot:', {
//      keys      : Object.keys(updatedDependencies),
//      sampleSet : Object.fromEntries(
//        Object.entries(updatedDependencies).slice(0, 3)
//      ),
//    });

    return changed ? updatedDependencies : prevDependencies;
  });
}, [dropdownOptions, formId, loading, hasHydratedFromFirestore, effectiveViewMode]);


const prevOptionsRef = useRef(dropdownOptions);


useEffect(() => {
  if (!formId || !dropdownOptions || Object.keys(dropdownOptions).length === 0) return;

  // IMPORTANT:
  // Dependencies are form-specific, but dropdown options may now come from:
  //  • local form dropdowns
  //  • org-managed runtime overlays
  //
  // We still allow re-fetch when options materially change, but we avoid
  // unnecessary churn during managed-option overlay refreshes by requiring
  // a real deep change.
  const changed = !isEqual(prevOptionsRef.current, dropdownOptions);

//  console.log('[DDC:dropdownOptionsEffect] Checking dropdownOptions change', {
//    formId,
//    changed,
//    prevKeys: Object.keys(prevOptionsRef.current || {}),
//    nextKeys: Object.keys(dropdownOptions || {}),
//  });

  if (changed) {
    prevOptionsRef.current = dropdownOptions;
    fetchDropdownDependencies();
  }
}, [formId, dropdownOptions, fetchDropdownDependencies, effectiveViewMode]);


  const contextValue = {
    dropdownDependencies,
    loading,
    loadingMap,             //  per‑dropdown loading state
    error,
    dynamicDropdownOptions, // Expose dynamically updated dropdown options
    getDependenciesForOption,
    dependencyFlags, // Expose dependencyFlags for use in FormListsTable
    getFilteredOptions, // Retrieve filtered options for dependent fields
    notifyDependentFields, // Trigger updates in dependent fields
    updateDependenciesForOption, // Update dependencies in state and local storage
    loadDependenciesForField, // Load dependencies for a specific field when needed
    fetchDropdownDependencies, //  Replace `saveAllDropdownDependencies` with Firestore fetch logic
    saveDropdownDependenciesToStorage, //  Save dependencies to local storage instead of Firestore directly
  };


  return (
    <DropdownDependenciesContext.Provider value={contextValue}>
      {children}
    </DropdownDependenciesContext.Provider>
  );
};
